/* Wraps mpegts.js so the rest of the player can stay codec-agnostic.
   Resolves on MEDIA_INFO (the first sign that demux is actually producing
   playable bytes); rejects on JS-level errors or the silent-MSE-close case
   that happens with MPEG-2-inside-TS (12 s watchdog + <video> error
   listener catch it). */

export async function attachMpegts(videoElement, url, { signal } = {}) {
  if (typeof MediaSource === 'undefined') throw new Error('MediaSource unsupported');
  // eslint-disable-next-line import/no-extraneous-dependencies
  const mod = await import('mpegts.js');
  const mpegts = mod.default || mod;
  if (!mpegts.isSupported()) throw new Error("Browser can't transmux MPEG-TS");

  return new Promise((resolve, reject) => {
    let aborted = false;
    let resolved = false;

    const player = mpegts.createPlayer(
      {
        type: 'mpegts',
        url,
        isLive: false,
        cors: true,
        withCredentials: false,
      },
      {
        enableStashBuffer: true,
        stashInitialSize: 1024 * 1024,
        enableWorker: false,
      },
    );

    const teardown = () => {
      try { player.unload(); } catch { /* ignore */ }
      try { player.detachMediaElement(); } catch { /* ignore */ }
      try { player.destroy(); } catch { /* ignore */ }
    };

    if (signal) {
      if (signal.aborted) {
        teardown();
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      signal.addEventListener(
        'abort',
        () => {
          aborted = true;
          if (!resolved) {
            teardown();
            reject(new DOMException('Aborted', 'AbortError'));
          }
        },
        { once: true },
      );
    }

    // MPEG-2-in-TS: mpegts.js demuxes fine, but MSE refuses the codec and
    // silently closes. We catch that via the <video> error event.
    const onVideoError = () => {
      if (resolved || aborted) return;
      videoElement.removeEventListener('error', onVideoError);
      teardown();
      reject(new Error("Codec inside MPEG-TS isn't decodable (likely MPEG-2)"));
    };
    videoElement.addEventListener('error', onVideoError);

    const codecTimeout = setTimeout(() => {
      if (resolved || aborted) return;
      videoElement.removeEventListener('error', onVideoError);
      teardown();
      reject(new Error('MPEG-TS produced no playable media in 12s'));
    }, 12000);

    const cleanup = () => {
      clearTimeout(codecTimeout);
      videoElement.removeEventListener('error', onVideoError);
    };

    player.on(mpegts.Events.ERROR, (type, detail, info) => {
      if (aborted || resolved) return;
      cleanup();
      teardown();
      const rawMsg = info?.msg || '';
      const isCors = /networkerror/i.test(type) || /failed to fetch|cors/i.test(rawMsg);
      reject(new Error(
        isCors
          ? 'Source missing CORS headers — mpegts.js needs JS-level fetch access'
          : `mpegts.js error — ${type}/${detail}: ${rawMsg}`,
      ));
    });

    player.on(mpegts.Events.MEDIA_INFO, (info) => {
      if (resolved || aborted) return;
      resolved = true;
      cleanup();
      resolve({ player, info, mpegts });
    });

    player.attachMediaElement(videoElement);
    player.load();
  });
}
